// api/resource.js — 읽기 상태를 **판별 가능한 하나의 모양**으로 만든다.
//
// QA v2 반려에서 구조적 원인이 같았던 결함 셋을 여기서 끝낸다.
//
//   D-4  권한 없는 Space 에 셸을 그대로 그리고 거짓 문장을 보여 줬다
//        → `ready` 가 아니면 children 을 **아예 렌더하지 않는다**. 화면이 "일단 그리고 나중에
//          지우는" 선택을 할 수 없다.
//   D-9  표면을 옮겨도 다시 읽지 않아 참여 신청이 "없음"으로 굳었다
//        → key 기반 캐시 + 명시적 `invalidate()`. 쓰기 성공은 반드시 무언가를 무효화한다.
//   D-2  서버엔 기록됐는데 화면은 실패처럼 보였다
//        → 낙관적 상태가 없다. 표시되는 것은 서버가 마지막으로 말한 것뿐이다.
//
// 기본 읽기는 수동이다. quiet 옵션을 준 mounted 읽기만 조용히 재검증한다.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { freshnessScheduler } from './freshness.js';
import { useWriteGate } from './write-boundary.js';
import { allowsAutoRetry, ProductError } from './errors.js';

/** 리소스 상태. 이 넷 말고는 없다. */
export const STATUS = Object.freeze({
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
});

// ── 캐시 · 무효화 ──────────────────────────────────────────────────────────

/** key → { data, at } */
const cache = new Map();
/** key → Set<listener> */
const listeners = new Map();

function subscribe(key, fn) {
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(fn);
  return () => {
    set.delete(fn);
    if (set.size === 0) listeners.delete(key);
  };
}

/**
 * 이 접두사로 시작하는 모든 리소스를 무효화하고, 화면에 붙어 있는 것은 즉시 다시 읽는다.
 *
 * 접두사 규약(넓은 것 → 좁은 것):
 *   `space:<id>`             그 Space 의 모든 읽기
 *   `space:<id>:threads`     대화 목록
 *   `space:<id>:task:<id>`   그 Task 의 context pack 과 모든 per-task 읽기
 *   `space:<id>:task:<id>:liveness`  그 Task 의 Ledger liveness
 *   `entry`                  첫 진입
 */
export function resourceKeyMatches(prefix, key) {
  return key === prefix || key.startsWith(`${prefix}:`);
}

/**
 * Task 아래 읽기는 반드시 이 계층을 쓴다. 부모 Task를 무효화하면 Context Pack과
 * liveness처럼 나중에 추가되는 모든 per-task 자원도 함께 다시 읽힌다.
 */
export function taskResourceKey(spaceId, taskId, child = null) {
  const root = `space:${spaceId}:task:${taskId}`;
  return child === null ? root : `${root}:${child}`;
}

export function invalidate(prefix) {
  freshnessScheduler().invalidate((key) => resourceKeyMatches(prefix, key));
  for (const key of [...cache.keys()]) {
    if (resourceKeyMatches(prefix, key)) cache.delete(key);
  }
  for (const [key, set] of listeners) {
    if (resourceKeyMatches(prefix, key)) {
      for (const fn of set) fn();
    }
  }
}

/** 전부 버린다. Node 를 바꾸거나 로그아웃할 때만. */
export function invalidateAll() {
  freshnessScheduler().clear();
  cache.clear();
  for (const set of listeners.values()) {
    for (const fn of set) fn();
  }
}

// ── 읽기 훅 ────────────────────────────────────────────────────────────────

/**
 * @param {string|null} key    캐시·무효화 키. `null` 이면 아직 읽을 때가 아니다(의존값 대기).
 * @param {(signal: AbortSignal) => Promise<any>} loader
 * @returns {{ status, data, error, reload, isStale }}
 */
export function useResource(key, loader, { quiet = false, identity = '' } = {}) {
  const quietResource = useQuietResource(quiet ? key : null, identity, loader);
  if (quiet) key = null;
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const [state, setState] = useState(() =>
    key !== null && cache.has(key)
      ? { status: STATUS.READY, data: cache.get(key).data, error: null }
      : { status: STATUS.LOADING, data: null, error: null },
  );
  // 캐시된 값을 보여 주는 동안 뒤에서 다시 읽고 있나 — 자리를 유지하기 위한 표시.
  const [isStale, setIsStale] = useState(false);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (key === null) return undefined;
    return subscribe(key, reload);
  }, [key, reload]);

  useEffect(() => {
    if (key === null) {
      setState({ status: STATUS.LOADING, data: null, error: null });
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    // 자동 재시도는 `space_not_projected` 한 경우만, 한 번만 (ux §5 공통 규칙 2).
    let autoRetriesLeft = 1;

    const cached = cache.get(key);
    if (cached) {
      setState({ status: STATUS.READY, data: cached.data, error: null });
      setIsStale(true);
    } else {
      setState({ status: STATUS.LOADING, data: null, error: null });
      setIsStale(false);
    }

    const run = async () => {
      try {
        const data = await loaderRef.current(controller.signal);
        if (cancelled) return;
        cache.set(key, { data, at: Date.now() });
        setState({ status: STATUS.READY, data, error: null });
        setIsStale(false);
      } catch (error) {
        if (cancelled || controller.signal.aborted) return;
        if (autoRetriesLeft > 0 && allowsAutoRetry(error)) {
          autoRetriesLeft -= 1;
          setTimeout(run, 900);
          return;
        }
        // 실패하면 **낡은 값을 계속 보여 주지 않는다.** 그게 D-4 가 났던 방식이다.
        cache.delete(key);
        setState({ status: STATUS.ERROR, data: null, error: asProductError(error) });
        setIsStale(false);
      }
    };

    run();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [key, nonce]);

  return quiet ? quietResource : { ...state, isStale, reload };
}

// The quiet store is mounted-only and identity-scoped; it never reads the manual
// cache. A key/Node change cannot render the preceding key even for one frame.
function useQuietResource(key, identity, loader) {
  const id = JSON.stringify([identity, key]);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const subscription = useRef(null);
  const [, update] = useState(0);
  useLayoutEffect(() => {
    if (key === null) return undefined;
    const handle = freshnessScheduler().subscribe(id, key,
      (signal) => loaderRef.current(signal), () => update((n) => n + 1));
    subscription.current = { id, handle };
    update((n) => n + 1);
    return () => { handle.unsubscribe(); subscription.current = null; };
  }, [id]);
  const current = () => subscription.current?.id === id ? subscription.current.handle : null;
  const state = current()?.get() ?? { status: STATUS.LOADING, data: null, error: null, isStale: true };
  return { ...state, error: state.error ? asProductError(state.error) : null,
    canWrite: () => current()?.canWrite() ?? false,
    reload: () => current()?.reload() };
}

/**
 * 여러 읽기를 한 화면 단위로 묶는다.
 *
 * **하나라도 실패하면 전체가 실패인 것은 아니다.** 어떤 것이 필수(required)이고 어떤 것이
 * 곁가지(optional)인지 화면이 정한다 — 부분 실패는 부분에 표시한다(ux §5 공통 규칙 3).
 */
export function combine(required, optional = {}) {
  const failed = Object.values(required).find((r) => r.status === STATUS.ERROR);
  if (failed) return { status: STATUS.ERROR, error: failed.error, data: null, optional };

  const loading = Object.values(required).some((r) => r.status === STATUS.LOADING);
  if (loading) return { status: STATUS.LOADING, error: null, data: null, optional };

  const data = {};
  for (const [name, r] of Object.entries(required)) data[name] = r.data;
  return { status: STATUS.READY, error: null, data, optional };
}

// ── 쓰기 훅 ────────────────────────────────────────────────────────────────

/**
 * 명령 하나를 화면에 붙인다.
 *
 * 규칙 셋을 훅이 강제한다:
 *  1. **낙관적 성공 금지** — `done` 은 서버 응답 뒤에만 true 가 된다.
 *  2. **키 고정** — 결과를 모르는 재시도(네트워크 실패·시간 초과)는 같은
 *     `Idempotency-Key` 를 쓴다. 반면 서버가 error code를 담아 확정 거절한 4xx는
 *     실행되지 않은 요청이므로 키를 버린다. 사람이 폼을 고쳐 다시 낼 때 이전 내용의
 *     키를 재사용하면 `idempotency_key_reused` 409이 나기 때문이다.
 *  3. **성공은 반드시 무효화한다** — `invalidates` 를 안 주면 화면이 서버와 갈린다(D-9).
 *
 * @param {(params:any)=>Promise<any>} runner
 * @param {{ invalidates?: string[] }} options
 */
export function useCommand(runner, { invalidates = [] } = {}) {
  const gate = useWriteGate();
  const gateRef = useRef(gate);
  gateRef.current = gate;
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const keyRef = useRef(null);
  const runnerRef = useRef(runner);
  runnerRef.current = runner;

  const reset = useCallback(() => {
    keyRef.current = null;
    setError(null);
    setDone(false);
  }, []);

  const run = useCallback(
    async (params) => {
      if (pending || !gateRef.current()) return { ok: false, error: null };
      // 이 행위의 키를 처음 시작할 때 한 번만 만든다.
      if (keyRef.current === null) keyRef.current = crypto.randomUUID();

      setPending(true);
      setError(null);
      try {
        const result = await runnerRef.current(params, keyRef.current);
        // 성공했으니 이 행위는 끝났다 — 다음 행위는 새 키를 받는다.
        keyRef.current = null;
        for (const prefix of invalidates) invalidate(prefix);
        setDone(true);
        return { ok: true, data: result };
      } catch (caught) {
        const err = asProductError(caught);
        // 서버가 code를 담아 답한 4xx는 결과가 확정됐다. 네트워크/시간 초과처럼
        // 결과를 모르는 경우에만 키를 붙들어 재시도한다.
        if (isDefinitiveRejection(err)) {
          keyRef.current = null;
        }
        setError(err);
        return { ok: false, error: err };
      } finally {
        setPending(false);
      }
    },
    // invalidates 는 렌더마다 새 배열일 수 있으므로 내용으로 비교한다.
    [pending, invalidates.join('|')],
  );

  return { run, pending, error, done, reset, blocked: !gate() };
}

function asProductError(error) {
  if (error instanceof ProductError) return error;
  return new ProductError({
    code: null,
    status: null,
    message: error?.message ?? String(error),
  });
}

function isDefinitiveRejection(error) {
  return error.status >= 400 && error.status < 500 && typeof error.code === 'string' && error.code !== '';
}
